import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { getPostBySlug, getRelatedPosts } from '../data/blogPosts';

const BlogPostPage = () => {
  const { slug } = useParams();
  const post = getPostBySlug(slug);
  const relatedPosts = getRelatedPosts(post?.id);

  if (!post) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-[#333333] mb-4">Post Not Found</h1>
            <Link to="/blog" className="text-[#FF6600] hover:text-[#FF9933]">
              Return to Blog
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>{post.title} | EquipEASE Blog</title>
        <meta name="description" content={post.metaDescription} />
        <meta name="keywords" content={post.keywords} />
        <link rel="canonical" href={`https://equipease.ca/blog/${post.slug}`} />
        
        {/* Open Graph */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.metaDescription} />
        <meta property="og:image" content={post.image} />
        <meta property="article:published_time" content={post.date} />
        <meta property="article:author" content={post.author} />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.metaDescription} />
        <meta name="twitter:image" content={post.image} />
        
        {/* Structured Data for Article */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": post.title,
            "image": post.image,
            "datePublished": post.date,
            "dateModified": post.date,
            "author": {
              "@type": "Organization",
              "name": post.author
            },
            "publisher": {
              "@type": "Organization",
              "name": "EquipEASE Lease Co.",
              "logo": {
                "@type": "ImageObject",
                "url": "https://customer-assets.emergentagent.com/job_equipease-pro/artifacts/k9cg4lku_3partlogoEE%20%28500%20x%20250%20px%29.png"
              }
            },
            "description": post.metaDescription,
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": `https://equipease.ca/blog/${post.slug}`
            }
          })}
        </script>
      </Helmet>
      
      <Header />
      
      <article className="flex-1 py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Breadcrumb */}
            <nav className="mb-8 text-sm">
              <Link to="/" className="text-[#4A4A4A] hover:text-[#FF6600]">Home</Link>
              <span className="mx-2 text-[#4A4A4A]">/</span>
              <Link to="/blog" className="text-[#4A4A4A] hover:text-[#FF6600]">Blog</Link>
              <span className="mx-2 text-[#4A4A4A]">/</span>
              <span className="text-[#333333]">{post.category}</span>
            </nav>

            {/* Post Header */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-8">
              <img
                src={post.image}
                alt={post.title}
                className="w-full h-96 object-cover"
              />
              
              <div className="p-8 md:p-12">
                <div className="flex items-center gap-4 mb-6">
                  <span className="bg-[#FF6600] text-white px-4 py-2 rounded-full text-sm font-semibold">
                    {post.category}
                  </span>
                  <span className="text-[#4A4A4A]">
                    {new Date(post.date).toLocaleDateString('en-CA', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </span>
                </div>

                <h1 className="text-4xl md:text-5xl font-bold text-[#333333] mb-6">
                  {post.title}
                </h1>

                <p className="text-xl text-[#4A4A4A] mb-8 leading-relaxed">
                  {post.excerpt}
                </p>

                <div className="flex items-center gap-4 pb-8 border-b border-gray-200">
                  <span className="text-sm text-[#4A4A4A]">By {post.author}</span>
                </div>
              </div>
            </div>

            {/* Post Content */}
            <div className="bg-white rounded-lg shadow-lg p-8 md:p-12 mb-8">
              <div 
                className="prose prose-lg max-w-none"
                dangerouslySetInnerHTML={{ __html: post.content }}
                style={{
                  color: '#4A4A4A',
                  lineHeight: '1.8'
                }}
              />

              {/* Tags */}
              <div className="mt-12 pt-8 border-t border-gray-200">
                <h3 className="text-sm font-semibold text-[#333333] mb-4">TAGS:</h3>
                <div className="flex flex-wrap gap-2">
                  {post.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="bg-[#FF6600]/10 text-[#FF6600] px-4 py-2 rounded-full text-sm font-medium"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* CTA Box */}
            <div className="bg-gradient-to-r from-[#FF6600] to-[#FF9933] rounded-lg p-8 md:p-12 text-white mb-12">
              <h2 className="text-3xl font-bold mb-4">Need Equipment Financing?</h2>
              <p className="text-xl mb-6">
                Get competitive rates and flexible terms for your business. Our experts are ready to help you find the perfect financing solution.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="tel:1-844-250-3273"
                  className="bg-white text-[#FF6600] px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-colors text-center"
                >
                  Call 1-844-250-EASE
                </a>
                <Link
                  to="/#contact"
                  className="bg-[#4A4A4A] hover:bg-[#333333] text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors text-center"
                >
                  Contact Us
                </Link>
              </div>
            </div>

            {/* Related Posts */}
            {relatedPosts.length > 0 && (
              <div>
                <h2 className="text-3xl font-bold text-[#333333] mb-8">Related Articles</h2>
                <div className="grid md:grid-cols-3 gap-6">
                  {relatedPosts.map((relatedPost) => (
                    <Link
                      key={relatedPost.id}
                      to={`/blog/${relatedPost.slug}`}
                      className="bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow overflow-hidden group"
                    >
                      <img
                        src={relatedPost.image}
                        alt={relatedPost.title}
                        className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="p-6">
                        <h3 className="text-lg font-bold text-[#333333] mb-2 group-hover:text-[#FF6600] transition-colors line-clamp-2">
                          {relatedPost.title}
                        </h3>
                        <p className="text-[#4A4A4A] text-sm line-clamp-2">
                          {relatedPost.excerpt}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </article>
      
      <Footer />
    </div>
  );
};

export default BlogPostPage;
